import { CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

export interface FieldDoc {
  field: string;
  dbColumn: string;
  type: string;
  required: boolean;
  source: 'manual' | 'hubspot' | 'apollo' | 'linkedin' | 'system' | 'manual+sync';
  description: string;
  usage: string;
  doNot?: string;
}

export const COMPANY_FIELDS: FieldDoc[] = [
  {
    field: 'Name',
    dbColumn: 'name',
    type: 'varchar(255)',
    required: true,
    source: 'manual+sync',
    description: 'Official company name.',
    usage: 'Use the legal/official name. For ATEK accounts prefixed with numbers (e.g. "0034 MUHC"), keep the prefix for backward compatibility with HubSpot.',
    doNot: 'Do not use abbreviations unless that is the official name.',
  },
  {
    field: 'Type',
    dbColumn: 'type',
    type: 'varchar(30)',
    required: false,
    source: 'manual',
    description: 'Relationship type: customer, prospect, supplier, partner, competitor, irrelevant, other.',
    usage: 'customer = pays for ATEK services (has an active account). prospect = potential client, not yet engaged. supplier = sells products/services to ATEK. partner = reseller, integrator, or strategic ally. competitor = offers similar services (monitoring, IoT, compliance). irrelevant = no business relevance (spam, out of scope). other = none of the above.',
    doNot: 'Do not set to "customer" until they are actually paying for ATEK services. Do not leave as default "customer" after HubSpot import — review and tag correctly.',
  },
  {
    field: 'Status',
    dbColumn: 'status',
    type: 'varchar(20)',
    required: false,
    source: 'manual',
    description: 'Lifecycle status: active, inactive, archived.',
    usage: 'Default "active". Set to "inactive" if the company is no longer engaged. Use "archived" for companies we no longer want to see in lists (soft-deleted from view).',
    doNot: 'Do not delete companies. Always archive instead to preserve history.',
  },
  {
    field: 'Industry',
    dbColumn: 'industry',
    type: 'varchar(100)',
    required: false,
    source: 'hubspot',
    description: 'Industry classification (e.g. HOSPITAL_HEALTH_CARE, GOVERNMENT_ADMINISTRATION).',
    usage: 'Auto-populated from HubSpot import. Uses HubSpot industry codes. Can be edited manually for companies not in HubSpot.',
    doNot: 'Do not invent custom industry codes. Stick to HubSpot standard values for consistency.',
  },
  {
    field: 'Domain',
    dbColumn: 'domain',
    type: 'varchar(255)',
    required: false,
    source: 'hubspot',
    description: 'Primary web domain (e.g. "atek.io"). Used as the unique dedup key across HubSpot and Apollo.',
    usage: 'Always set when importing from HubSpot/Apollo. Used to match contacts by email domain. Critical for deduplication.',
    doNot: 'Do not include protocol (no "https://"). Do not include "www.".',
  },
  {
    field: 'Website',
    dbColumn: 'website',
    type: 'varchar(255)',
    required: false,
    source: 'hubspot',
    description: 'Full website URL.',
    usage: 'Include the protocol (https://). Can differ from domain if the company uses a subdomain or different URL.',
  },
  {
    field: 'Phone',
    dbColumn: 'phone',
    type: 'varchar(50)',
    required: false,
    source: 'hubspot',
    description: 'Main company phone number.',
    usage: 'Store in the format provided by the source (HubSpot, Apollo). No normalization required.',
  },
  {
    field: 'Email',
    dbColumn: 'email',
    type: 'varchar(255)',
    required: false,
    source: 'manual',
    description: 'General company email (e.g. info@company.com).',
    usage: 'Use for general inquiries only. Individual contacts should have their own email in the contacts table.',
    doNot: 'Do not store personal emails here. Use the contacts table for person-specific emails.',
  },
  {
    field: 'Address / City / Province / Country / Postal Code',
    dbColumn: 'address, city, province, country, postal_code',
    type: 'text + varchar',
    required: false,
    source: 'hubspot',
    description: 'Company physical address fields.',
    usage: 'Populated from HubSpot/Apollo imports. Province maps to HubSpot "state" field. Country defaults to "CA" (Canada).',
  },
  {
    field: 'LinkedIn URL',
    dbColumn: 'linkedin_url',
    type: 'varchar(500)',
    required: false,
    source: 'apollo',
    description: 'Company LinkedIn page URL.',
    usage: 'Full URL including https://. Populated from Apollo imports. Useful for prospection research.',
  },
  {
    field: 'Employee Count',
    dbColumn: 'employee_count',
    type: 'integer',
    required: false,
    source: 'hubspot',
    description: 'Approximate number of employees.',
    usage: 'Used for company segmentation and lead scoring. Populated from HubSpot (numberofemployees) or Apollo (estimated_num_employees).',
    doNot: 'Do not manually estimate. Leave null if unknown.',
  },
  {
    field: 'Annual Revenue',
    dbColumn: 'annual_revenue',
    type: 'numeric(15,2)',
    required: false,
    source: 'hubspot',
    description: 'Estimated annual revenue in dollars.',
    usage: 'Used for lead prioritization. Populated from HubSpot (annualrevenue) or Apollo (organization_revenue). Stored in raw dollars, not thousands.',
    doNot: 'Do not store in thousands or millions. Store the full amount (e.g. 1000000000 for $1B).',
  },
  {
    field: 'HubSpot Company ID',
    dbColumn: 'hubspot_company_id',
    type: 'varchar(50)',
    required: false,
    source: 'system',
    description: 'HubSpot CRM record ID for this company.',
    usage: 'Auto-set during HubSpot import. Used for sync and deduplication. Do not edit manually.',
    doNot: 'Never edit this field manually. It is the link between our CRM and HubSpot.',
  },
  {
    field: 'Apollo Company ID',
    dbColumn: 'apollo_company_id',
    type: 'varchar(50)',
    required: false,
    source: 'system',
    description: 'Apollo.io organization ID.',
    usage: 'Auto-set during Apollo import. Used for sync and deduplication.',
    doNot: 'Never edit this field manually.',
  },
  {
    field: 'Account ID',
    dbColumn: 'account_id',
    type: 'uuid',
    required: false,
    source: 'manual',
    description: 'Link to an existing ATEK account (PostgreSQL accounts table).',
    usage: 'Set when a company becomes an ATEK customer and has a corresponding account. Allows bridging CRM companies to the accounts system.',
    doNot: 'Do not set for prospects or leads that are not yet ATEK customers.',
  },
  {
    field: 'Notes',
    dbColumn: 'notes',
    type: 'text',
    required: false,
    source: 'manual+sync',
    description: 'Free-text notes about the company.',
    usage: 'HubSpot imports populate this from the "description" field. Can be edited manually to add context.',
  },
  {
    field: 'Tags',
    dbColumn: 'tags',
    type: 'text[]',
    required: false,
    source: 'manual',
    description: 'Array of string tags for categorization.',
    usage: 'Use for custom grouping (e.g. "pharma", "quebec", "tier-1"). Keep tags lowercase and hyphenated.',
  },
];

export const CONTACT_FIELDS: FieldDoc[] = [
  {
    field: 'First Name / Last Name',
    dbColumn: 'first_name, last_name',
    type: 'varchar(100)',
    required: true,
    source: 'manual+sync',
    description: 'Contact full name, split into first and last.',
    usage: 'Both are required. Populated from HubSpot/Apollo imports or manual entry.',
  },
  {
    field: 'Company',
    dbColumn: 'company_id',
    type: 'uuid (FK)',
    required: false,
    source: 'manual',
    description: 'Link to the companies table. Nullable — a contact can exist independently.',
    usage: 'Set when the contact belongs to a known company. The join provides company_name in list views.',
    doNot: 'Do not create duplicate companies just to link a contact. Search existing companies first.',
  },
  {
    field: 'Email',
    dbColumn: 'email',
    type: 'varchar(255)',
    required: false,
    source: 'manual+sync',
    description: 'Professional email address.',
    usage: 'Primary communication channel. Use the business email, not personal Gmail/Yahoo.',
  },
  {
    field: 'Phone / Mobile Phone',
    dbColumn: 'phone, mobile_phone',
    type: 'varchar(30)',
    required: false,
    source: 'manual+sync',
    description: 'Office phone and mobile phone.',
    usage: 'Phone = office/desk line. Mobile = cell phone. Both are optional.',
  },
  {
    field: 'Title',
    dbColumn: 'title',
    type: 'varchar(100)',
    required: false,
    source: 'manual+sync',
    description: 'Job title (e.g. "Director of Pharmacy", "VP Operations").',
    usage: 'Important for lead qualification. Populated from Apollo/HubSpot.',
  },
  {
    field: 'Department',
    dbColumn: 'department',
    type: 'varchar(100)',
    required: false,
    source: 'manual+sync',
    description: 'Department within the company.',
    usage: 'Helps identify the right stakeholder. Common: Pharmacy, IT, Operations, Engineering.',
  },
  {
    field: 'Is Primary',
    dbColumn: 'is_primary',
    type: 'boolean',
    required: false,
    source: 'manual',
    description: 'Marks the main contact for a company.',
    usage: 'Set to true for the primary decision-maker or main point of contact at a company. Only one contact per company should be primary.',
    doNot: 'Do not mark multiple contacts as primary for the same company.',
  },
  {
    field: 'Source',
    dbColumn: 'source',
    type: 'varchar(30)',
    required: false,
    source: 'system',
    description: 'How the contact was created: manual, hubspot, apollo, linkedin.',
    usage: 'Auto-set based on the import method. Helps track lead generation channels.',
  },
  {
    field: 'Status',
    dbColumn: 'status',
    type: 'varchar(30)',
    required: false,
    source: 'manual',
    description: 'Contact lifecycle: prospect, active, inactive.',
    usage: 'Default "prospect". Set to "active" when engaged. Set to "inactive" if no longer relevant.',
  },
  {
    field: 'LinkedIn Fields',
    dbColumn: 'linkedin_url, linkedin_headline, linkedin_about, linkedin_followers, linkedin_degree, linkedin_avatar_url, linkedin_work_history, linkedin_education, linkedin_skills, linkedin_languages, linkedin_scraped_at',
    type: 'various',
    required: false,
    source: 'linkedin',
    description: 'LinkedIn profile data scraped by the LinkedIn automation service.',
    usage: 'Auto-populated by the LinkedIn scraper. linkedin_url is the unique key (has a unique index). Work history, education, skills, and languages are stored as JSONB.',
    doNot: 'Do not edit these fields manually. They are overwritten by the next LinkedIn scrape.',
  },
  {
    field: 'HubSpot Contact ID',
    dbColumn: 'hubspot_contact_id',
    type: 'varchar(50)',
    required: false,
    source: 'system',
    description: 'HubSpot CRM contact record ID.',
    usage: 'Auto-set during HubSpot import. Used for sync and deduplication.',
    doNot: 'Never edit manually.',
  },
  {
    field: 'Apollo Contact ID',
    dbColumn: 'apollo_contact_id',
    type: 'varchar(50)',
    required: false,
    source: 'system',
    description: 'Apollo.io person ID.',
    usage: 'Auto-set during Apollo import.',
    doNot: 'Never edit manually.',
  },
  {
    field: 'Notes / Tags',
    dbColumn: 'notes, tags',
    type: 'text, text[]',
    required: false,
    source: 'manual',
    description: 'Free-text notes and string array tags.',
    usage: 'Same as company notes/tags. Use for manual context and categorization.',
  },
];

const SOURCE_COLORS: Record<string, string> = {
  manual: 'bg-gray-100 text-gray-800',
  hubspot: 'bg-orange-100 text-orange-800',
  apollo: 'bg-blue-100 text-blue-800',
  linkedin: 'bg-sky-100 text-sky-800',
  system: 'bg-purple-100 text-purple-800',
  'manual+sync': 'bg-green-100 text-green-800',
};

export function FieldDocsTable({ fields }: { fields: FieldDoc[] }) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[180px]">Field</TableHead>
            <TableHead className="w-[100px]">Source</TableHead>
            <TableHead className="w-[60px]">Req.</TableHead>
            <TableHead>Usage</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((f) => (
            <TableRow key={f.field}>
              <TableCell>
                <div className="font-medium text-sm">{f.field}</div>
                <div className="text-xs text-muted-foreground font-mono">{f.dbColumn}</div>
                <div className="text-xs text-muted-foreground">{f.type}</div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={SOURCE_COLORS[f.source] || ''}>
                  {f.source}
                </Badge>
              </TableCell>
              <TableCell>
                {f.required ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <span className="text-muted-foreground text-xs">opt.</span>
                )}
              </TableCell>
              <TableCell>
                <div className="text-sm">{f.description}</div>
                <div className="mt-1 text-sm text-muted-foreground flex items-start gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
                  {f.usage}
                </div>
                {f.doNot && (
                  <div className="mt-1 text-sm text-muted-foreground flex items-start gap-1">
                    <XCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                    {f.doNot}
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
